import MsgReader from '@kenjiuno/msgreader';
import Handlebars from 'handlebars';
import jsPDF from 'jspdf';
import { useCallback } from 'react';

const emailTemplate = Handlebars.compile(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  {{{htmlHead}}}
</head>
<body style="margin:0;padding:0;background:#fff;">
  <div style="padding:25px">
    <div>{{subject}}</div>
    <table>
      <tr>
        <td>From:</td>
        <td>{{senderName}} &lt;{{senderEmail}}&gt;</td>
      </tr>
      <tr>
        <td>To:</td>
        <td>{{recipients}}</td>
      </tr>
      <tr>
        <td>Cc:</td>
        <td>{{cc}}</td>
      </tr>
      <tr>
        <td>Date:</td>
        <td>{{date}}</td>
      </tr>
    </table>
  </div>
  <div style="padding:25px">{{{bodyContent}}}</div>
</body>
</html>`);

export interface MsgToPdfResult {
  convert: (file: File) => Promise<{ pdfBlob: Blob | null; error?: Error }>;
}

const buildEmailHtml = (fields: {
  subject: string;
  senderName: string;
  senderEmail: string;
  recipients: string;
  cc: string;
  date: string;
  body: string;
  htmlBytes: Uint8Array | null;
}): string => {
  let htmlHead = '';
  let bodyContent: string;

  if (fields.htmlBytes) {
    const decoded = new TextDecoder('utf-8').decode(fields.htmlBytes);

    // Pull <style> blocks out of the email HTML so we can keep them in <head>
    const styleMatches = decoded.match(/<style[^>]*>[\s\S]*?<\/style>/gi) ?? [];
    htmlHead = styleMatches.join('\n');

    // Extract the content inside <body>...</body> if present, otherwise use the whole fragment
    const bodyMatch = decoded.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    bodyContent = bodyMatch ? bodyMatch[1] : decoded.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  } else {
    bodyContent = `<div style="white-space:pre-wrap;font-family:Arial,sans-serif;font-size:13px;">${Handlebars.escapeExpression(
      fields.body,
    )}</div>`;
  }

  return emailTemplate({
    subject: fields.subject,
    senderName: fields.senderName,
    senderEmail: fields.senderEmail,
    recipients: fields.recipients,
    cc: fields.cc,
    date: fields.date,
    htmlHead,
    bodyContent,
  });
};

const formatRecipient = (recipient: { name?: string; smtpAddress?: string }) => {
  return recipient.name ? `${recipient.name} <${recipient.smtpAddress ?? ''}>` : recipient.smtpAddress ?? '';
};

export const useMsgToPdf = (): MsgToPdfResult => {
  const convert = useCallback(async (file: File): Promise<{ pdfBlob: Blob | null; error?: Error }> => {
    try {
      const buffer = await file.arrayBuffer();
      const reader = new MsgReader(buffer);
      const msg = reader.getFileData();
      const allRecipients = msg.recipients ?? [];
      const recipients = allRecipients
        .filter((recipient) => recipient.recipType !== 'cc' && recipient.recipType !== 'bcc')
        .map(formatRecipient)
        .join(', ');
      const cc = allRecipients
        .filter((recipient) => recipient.recipType === 'cc')
        .map(formatRecipient)
        .join(', ');
      const rawHtml = buildEmailHtml({
        subject: msg.subject ?? '(no subject)',
        senderName: msg.senderName ?? '',
        senderEmail: msg.senderSmtpAddress ?? msg.senderEmail ?? '',
        recipients,
        cc,
        date: msg.messageDeliveryTime ? new Date(msg.messageDeliveryTime).toLocaleString() : '',
        body: msg.body ?? '',
        htmlBytes: msg.html ?? null,
      });

      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      doc.setProperties({ title: file.name });

      const pdfBlob = await new Promise<Blob>((resolve, reject) => {
        doc
          .html(rawHtml, {
            callback: (pdf) => {
              resolve(pdf.output('blob'));
            },
            x: 0,
            y: 0,
            width: 612, //letter size
            windowWidth: 800, //browser viewport
            autoPaging: 'text',
          })
          .catch(reject);
      });

      return { pdfBlob };
    } catch (error) {
      console.error('useMsgToPdf', error);
      return { pdfBlob: null, error: new Error('Failed to convert .msg to PDF') };
    }
  }, []);

  return { convert };
};
